import { useState, useEffect, useCallback } from 'react';
import { CreditCard, DollarSign, TrendingUp, Battery, Zap } from 'lucide-react';
import { fetchRevenue, formatCents, type RevenueSummary } from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './RevenuePage.module.css';

function isoDate(d: Date): string {
  return d.toISOString().split('T')[0]!;
}

export function RevenuePage() {
  useRecentRoute('Revenue');
  const today = new Date();
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

  const [from, setFrom] = useState(isoDate(thirtyDaysAgo));
  const [to, setTo] = useState(isoDate(today));
  const [data, setData] = useState<RevenueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const summary = await fetchRevenue({
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59`).toISOString(),
      });
      setData(summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load revenue');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // Held payouts (collect and hold): 0 from an older API that lacks them.
  const heldCount = data?.held_count ?? 0;
  const heldGross = data?.held_cents ?? 0;
  const heldNet = data?.held_net_cents ?? 0;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><CreditCard size={18} /></div>
          <div>
            <h1 className={styles.title}>Revenue</h1>
            <p className={styles.subtitle}>Charging revenue and operator payouts</p>
          </div>
        </div>
        <div className={styles.dateFilter}>
          <input type="date" className={styles.dateInput} value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className={styles.dateSep}>→</span>
          <input type="date" className={styles.dateInput} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading && !data ? (
        <div className={styles.loading}>Loading...</div>
      ) : data ? (
        <>
          {/* Top KPIs */}
          <div className={styles.kpiGrid}>
            <KpiCard icon={DollarSign} label="Total revenue" value={formatCents(data.total_revenue_cents, data.currency)} accent="#2ecc71" sub={`${data.session_count} sessions`} />
            <KpiCard icon={TrendingUp} label="Owner payouts" value={formatCents(data.owner_payouts_cents, data.currency)} accent="#3498db" sub="paid to operators" />
            <KpiCard icon={Battery} label="Energy delivered" value={`${data.total_energy_kwh.toFixed(1)} kWh`} accent="#9b59b6" sub="total kWh sold" />
            <KpiCard icon={Zap} label="Platform fees" value={formatCents(data.platform_fees_cents, data.currency)} accent="#f39c12" sub="EVI's cut" />
          </div>

          {/* Breakdown waterfall */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Revenue breakdown</h2>
            <div className={styles.waterfall}>
              <div className={styles.waterfallRow}>
                <span>Gross revenue</span>
                <span className={styles.amount}>{formatCents(data.total_revenue_cents, data.currency)}</span>
              </div>
              <div className={`${styles.waterfallRow} ${styles.deduction}`}>
                <span>− Stripe processing fees</span>
                <span className={styles.amount}>{formatCents(-data.stripe_fees_cents, data.currency)}</span>
              </div>
              <div className={styles.waterfallRow}>
                <span>= Net revenue</span>
                <span className={styles.amount}>{formatCents(data.total_revenue_cents - data.stripe_fees_cents, data.currency)}</span>
              </div>
              {/* Collect and hold: sessions whose Site electricity exceeded
                  their energy revenue. The charge (less Stripe's fee) sits
                  on EVI's balance until a human pays the host and Site, so
                  it is neither EVI's fee nor a payout yet. Taken out here
                  so the rows below are the settled split only and the
                  waterfall still lands on owner payouts. */}
              {heldCount > 0 && (
                <div className={`${styles.waterfallRow} ${styles.deduction}`}>
                  <span>
                    − Held for manual payout
                    <span className={styles.rowNote}>
                      {heldCount} session{heldCount === 1 ? '' : 's'}, {formatCents(heldGross, data.currency)} charged less Stripe&apos;s fee; the host and Site are paid manually
                    </span>
                  </span>
                  <span className={styles.amount}>{formatCents(-heldNet, data.currency)}</span>
                </div>
              )}
              <div className={`${styles.waterfallRow} ${styles.deduction}`}>
                <span>− Electricity cost</span>
                <span className={styles.amount}>{formatCents(-data.electricity_cost_cents, data.currency)}</span>
              </div>
              <div className={styles.waterfallRow}>
                <span>= Revenue after electricity</span>
                <span className={styles.amount}>{formatCents(data.total_revenue_cents - data.stripe_fees_cents - heldNet - data.electricity_cost_cents, data.currency)}</span>
              </div>
              <div className={`${styles.waterfallRow} ${styles.deduction}`}>
                <span>− Platform fee (EVI)</span>
                <span className={styles.amount}>{formatCents(-data.platform_fees_cents, data.currency)}</span>
              </div>
              <div className={`${styles.waterfallRow} ${styles.totalRow}`}>
                <span>Owner payouts</span>
                <span className={styles.amount}>{formatCents(data.owner_payouts_cents, data.currency)}</span>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, sub }: {
  icon: React.ComponentType<{ size: number }>;
  label: string;
  value: string;
  accent: string;
  sub?: string;
}) {
  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiIconWrap} style={{ background: `${accent}15`, borderColor: `${accent}30` }}>
        <Icon size={18} />
      </div>
      <div>
        <p className={styles.kpiLabel}>{label}</p>
        <p className={styles.kpiValue}>{value}</p>
        {sub && <p className={styles.kpiSub}>{sub}</p>}
      </div>
    </div>
  );
}
